import React from "react"
import DeviceFrame from "components/elements/DeviceFrame"
import KioskPreviewBackdrop from "./KioskPreviewBackdrop"
import KioskPreviewHome from "./KioskPreviewHome"
import KioskPreviewScanScreen from "./KioskPreviewScanScreen"
import { KIOSK_SCAN_LABEL_FIT, KIOSK_TITLE_FIT, useFittedText } from "../kioskTextFit"
import { buildKioskLayout } from "../kioskLayout"
import {
  KIOSK_IPAD_LANDSCAPE_FRAME,
  KIOSK_IPAD_PORTRAIT_FRAME,
  KIOSK_IPHONE_FRAME,
} from "../kioskPreviewLayout"
import type { PreviewDevice, PreviewScreen } from "../kioskPreviewTypes"

export type KioskPreviewDeviceProps = {
  device: PreviewDevice
  screen: PreviewScreen
  accent: string
  title: string
  showTitle: boolean
  scanLabel: string
  registerLabel: string
  scanQrCodeEnabled: boolean
  walkInRegistrationEnabled: boolean
  backgroundUrl: string
  logoUrl: string
}

const FRAME_BY_DEVICE = {
  iphone: KIOSK_IPHONE_FRAME,
  ipadPortrait: KIOSK_IPAD_PORTRAIT_FRAME,
  ipadLandscape: KIOSK_IPAD_LANDSCAPE_FRAME,
} as const

/** Renders one kiosk screen inside the matching device frame, sized from the spec layout. */
const KioskPreviewDevice = ({
  device,
  screen,
  accent,
  title,
  showTitle,
  scanLabel,
  registerLabel,
  scanQrCodeEnabled,
  walkInRegistrationEnabled,
  backgroundUrl,
  logoUrl,
}: KioskPreviewDeviceProps) => {
  const frame = FRAME_BY_DEVICE[device]
  const hasLogo = Boolean(logoUrl)
  const hasTitle = showTitle && Boolean(title)

  const layoutInput = {
    device,
    spec: frame.spec,
    frameWidthPx: frame.frameWidthPx,
    hasLogo,
    hasTitle,
    scanQrCodeEnabled,
    walkInRegistrationEnabled,
  }

  // Fit the title and scan label once each (font + line count). Font drives rendering; the
  // line count is fed back in so the layout reserves the right height (a wrapped 2nd line
  // would otherwise overflow into the buttons / camera box). Width and font don't depend on
  // the line count, so this first (1-line) build gives valid measurements for the final one.
  const base = buildKioskLayout(layoutInput)
  const titleFit = useFittedText(
    title,
    base.home.brand.titleFontPx,
    base.home.brand.width,
    KIOSK_TITLE_FIT,
    hasTitle && screen === "home",
  )
  const scanFit = useFittedText(
    scanLabel,
    base.scan.group.labelFontPx,
    base.scan.group.width,
    KIOSK_SCAN_LABEL_FIT,
    screen === "scan",
  )
  const layout = titleFit.lines > 1 || scanFit.lines > 1
    ? buildKioskLayout({
      ...layoutInput,
      titleLines: titleFit.lines,
      scanLabelLines: scanFit.lines,
    })
    : base

  return (
    <DeviceFrame spec={frame.spec} w={`${frame.frameWidthPx}px`}>
      <KioskPreviewBackdrop backgroundUrl={backgroundUrl} accent={accent}>
        {screen === "home" ? (
          <KioskPreviewHome
            layout={layout.home}
            accent={accent}
            title={title}
            titleFontPx={titleFit.fontPx}
            scanLabel={scanLabel}
            registerLabel={registerLabel}
            scanQrCodeEnabled={scanQrCodeEnabled}
            walkInRegistrationEnabled={walkInRegistrationEnabled}
            logoUrl={logoUrl}
          />
        ) : (
          <KioskPreviewScanScreen
            layout={layout.scan}
            title={title}
            logoUrl={logoUrl}
            scanLabel={scanLabel}
            labelFontPx={scanFit.fontPx}
          />
        )}
      </KioskPreviewBackdrop>
    </DeviceFrame>
  )
}

export default KioskPreviewDevice
